import React, { useState, useEffect, useMemo } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ChevronDown, Star, Rocket, Compass, Activity } from 'lucide-react';
import { cn } from "@/lib/utils";
import { base44 } from "@/api/base44Client";

export default function NodeSelector({ 
  viewLevel, 
  items, 
  onSelect,
  onDrillToObjective,
  onDrillToActivity,
  objectives = [], 
  activities = [],
  missions = []
}) {
  const [open, setOpen] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);

  useEffect(() => {
    base44.auth.me().then(user => setCurrentUserEmail(user?.email)).catch(() => {});
  }, []);

  // Determine which entity types to show based on view level
  const showObjectives = viewLevel === 'mission';
  const showActivities = viewLevel === 'mission' || viewLevel === 'objective';

  // Filter active items only
  const activeObjectives = objectives.filter(o => o.status !== 'archived');
  const activeActivities = activities.filter(a => a.status !== 'archived');

  // Check if user owns an item
  const isOwnedByUser = (item, type) => {
    if (!currentUserEmail) return false;
    if (type === 'mission') {
      return item.owner_email === currentUserEmail || item.sponsor_email === currentUserEmail;
    }
    if (type === 'objective') {
      return item.owner_email === currentUserEmail;
    }
    if (type === 'activity') {
      return item.responsible_email === currentUserEmail || item.accountable_email === currentUserEmail;
    }
    return false;
  };

  // Build options for current view level items
  const currentViewOptions = useMemo(() => {
    if (viewLevel === 'mission') {
      return missions.filter(m => m.status !== 'archived').map(m => ({
        value: m.id,
        code: m.mission_code,
        label: m.title,
        type: 'mission',
        isOwned: isOwnedByUser(m, 'mission')
      }));
    } else if (viewLevel === 'objective') {
      return activeObjectives.map(o => ({
        value: o.id,
        code: o.objective_code,
        label: o.statement,
        type: 'objective',
        isOwned: isOwnedByUser(o, 'objective')
      }));
    } else if (viewLevel === 'activity') {
      return activeActivities.map(a => ({
        value: a.id,
        code: a.activity_code,
        label: a.title,
        type: 'activity',
        isOwned: isOwnedByUser(a, 'activity')
      }));
    }
    return [];
  }, [viewLevel, missions, activeObjectives, activeActivities, currentUserEmail]);

  // Build options for objectives (when in mission view)
  const objectiveOptions = useMemo(() => {
    if (!showObjectives) return [];
    return activeObjectives.map(o => ({
      value: o.id,
      code: o.objective_code,
      label: o.statement,
      type: 'objective',
      missionId: o.mission_id,
      isOwned: isOwnedByUser(o, 'objective')
    }));
  }, [showObjectives, activeObjectives, currentUserEmail]);

  // Build options for activities (when in mission or objective view)
  const activityOptions = useMemo(() => {
    if (!showActivities) return [];
    return activeActivities.map(a => ({
      value: a.id,
      code: a.activity_code,
      label: a.title,
      type: 'activity',
      objectiveId: a.objective_id,
      isOwned: isOwnedByUser(a, 'activity')
    }));
  }, [showActivities, activeActivities, currentUserEmail]);

  const getPlaceholder = () => {
    if (viewLevel === 'mission') return 'Search missions, objectives, activities...';
    if (viewLevel === 'objective') return 'Search objectives or activities...';
    if (viewLevel === 'activity') return 'Search activities...';
    return 'Search...';
  };

  const handleSelect = (option) => {
    setOpen(false);
    
    if (option.type === 'activity' && viewLevel === 'activity') {
      // In activity view, always use drill to handle cross-objective navigation
      if (onDrillToActivity) {
        const activity = activeActivities.find(a => a.id === option.value);
        if (activity) {
          const objective = activeObjectives.find(o => o.id === activity.objective_id);
          const missionId = objective?.mission_id;
          onDrillToActivity(option.value, activity.objective_id, missionId);
        }
      }
    } else if (option.type === viewLevel) {
      // Same level - just select
      onSelect(option.value);
    } else if (option.type === 'objective' && viewLevel === 'mission') {
      // Drill from mission to objective
      if (onDrillToObjective) {
        onDrillToObjective(option.value, option.missionId);
      }
    } else if (option.type === 'activity') {
      // Drill to activity from mission or objective view
      if (onDrillToActivity) {
        const activity = activeActivities.find(a => a.id === option.value);
        if (activity) {
          const objective = activeObjectives.find(o => o.id === activity.objective_id);
          const missionId = objective?.mission_id;
          onDrillToActivity(option.value, activity.objective_id, missionId);
        }
      }
    }
  };

  const getIcon = (type) => {
    if (type === 'mission') return <Rocket className="w-3.5 h-3.5 text-amber-400" />;
    if (type === 'objective') return <Compass className="w-3.5 h-3.5 text-indigo-400" />;
    if (type === 'activity') return <Activity className="w-3.5 h-3.5 text-emerald-400" />;
    return null;
  };

  const renderOption = (option) => (
    <CommandItem
      key={`${option.type}-${option.value}`}
      value={`${option.code} ${option.label}`}
      onSelect={() => handleSelect(option)}
      className="flex items-center gap-2 cursor-pointer text-slate-100 hover:text-white"
    >
      {option.isOwned && <Star className="w-3 h-3 text-amber-400 fill-amber-400 shrink-0" />}
      {getIcon(option.type)}
      <span className="font-mono text-[10px] text-slate-400 shrink-0">[{option.code}]</span>
      <span className="truncate text-sm text-slate-100">{option.label}</span>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-slate-950 border-slate-700 text-slate-100 hover:bg-slate-900 hover:border-slate-600"
        >
          <span className="text-slate-400 text-sm">{getPlaceholder()}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0 bg-slate-950 border-slate-700" align="start">
        <Command className="bg-transparent">
          <CommandInput 
            placeholder="Type code or name to search..." 
            className="border-none focus:ring-0 text-white placeholder:text-slate-400"
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty className="py-6 text-center text-sm text-slate-500">
              No results found.
            </CommandEmpty>
            
            {/* Current View Level Items */}
            <CommandGroup heading={
              <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
                {getIcon(viewLevel)}
                {viewLevel === 'mission' ? 'Missions' : viewLevel === 'objective' ? 'Objectives' : 'Activities'}
              </span>
            }>
              {currentViewOptions.map(renderOption)}
            </CommandGroup>

            {/* Objectives (when in mission view) */}
            {showObjectives && objectiveOptions.length > 0 && (
              <>
                <CommandSeparator className="bg-slate-800" />
                <CommandGroup heading={
                  <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
                    <Compass className="w-3.5 h-3.5 text-indigo-400" />
                    Jump to Objective
                  </span>
                }>
                  {objectiveOptions.map(renderOption)}
                </CommandGroup>
              </>
            )}

            {/* Activities (when in mission or objective view) */}
            {showActivities && activityOptions.length > 0 && (
              <>
                <CommandSeparator className="bg-slate-800" />
                <CommandGroup heading={
                  <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
                    <Activity className="w-3.5 h-3.5 text-emerald-400" />
                    Jump to Activity
                  </span>
                }>
                  {activityOptions.map(renderOption)}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}